
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { invokeFunction } from '@/lib/utils/invokeFunction';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, Loader2 } from 'lucide-react';
import { runPremiumExpirationCheck } from '@/lib/actions/adminActions';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// Define an interface for cron jobs
interface CronJob {
  jobname: string;
  schedule: string;
  last_run?: string;
  next_run?: string;
}

const CronJobsManager = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isArchivingLoading, setIsArchivingLoading] = React.useState(false);
  const [isPremiumExpirationLoading, setIsPremiumExpirationLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // We won't try to get cron jobs from PostgreSQL directly as that's not supported via the Supabase client
  // Instead, we'll use hardcoded job information that matches what we set up in the edge functions
  const [cronJobs] = React.useState<CronJob[]>([
    {
      jobname: "archiving-cron",
      schedule: "0 7 * * *", // Every day at 7 AM
      last_run: "-", 
      next_run: "-"
    },
    {
      jobname: "permanent-deletion-cron",
      schedule: "0 8 * * *", // Every day at 8 AM
      last_run: "-",
      next_run: "-"
    },
    {
      jobname: "premium-expiration-cron",
      schedule: "0 0 * * *", // Every day at midnight
      last_run: "-",
      next_run: "-"
    }
  ]);

  const setupPermanentDeletionCron = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await invokeFunction('setup-permanent-deletion-cron', {});
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Permanent deletion cron job has been set up successfully",
      });
    } catch (error: any) {
      setError(`Failed to set up permanent deletion cron job: ${error.message}`);
      toast({
        title: "Error",
        description: "Failed to set up permanent deletion cron job",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupArchivingCron = async () => {
    setIsArchivingLoading(true);
    setError(null);
    try {
      const { data, error } = await invokeFunction('setup-archiving-cron', {});
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Archiving cron job has been set up successfully",
      });
    } catch (error: any) {
      setError(`Failed to set up archiving cron job: ${error.message}`);
      toast({
        title: "Error",
        description: "Failed to set up archiving cron job",
        variant: "destructive",
      });
    } finally {
      setIsArchivingLoading(false);
    }
  };

  const handlePremiumExpirationCheck = async () => {
    setIsPremiumExpirationLoading(true);
    setError(null);
    try {
      const result = await runPremiumExpirationCheck();
      
      if (result.success) {
        toast({
          title: "Success",
          description: `Premium status expired for ${result.expired_count} listings`,
        });
      } else {
        setError(`${result.error}${result.details ? ': ' + result.details : ''}`);
        throw new Error(result.error || "Unknown error");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to run premium expiration check: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsPremiumExpirationLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cron Jobs Manager</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        )}
        
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-500">
            Configure automated tasks for listing management
          </p>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Archiving Cron Job</h3>
              <p className="text-sm text-gray-500 mb-2">
                Runs daily at 7 AM to archive inactive listings older than 90 days
              </p>
              <Button 
                onClick={setupArchivingCron} 
                disabled={isArchivingLoading}
                className="w-full"
              >
                {isArchivingLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isArchivingLoading ? "Setting up..." : "Setup Archiving Cron"}
              </Button>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2">Permanent Deletion Cron Job</h3>
              <p className="text-sm text-gray-500 mb-2">
                Runs daily at 8 AM to permanently delete listings marked as deleted that are older than 80 days and have no active orders
              </p>
              <Button 
                onClick={setupPermanentDeletionCron}
                disabled={isLoading}
                variant="destructive"
                className="w-full"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLoading ? "Setting up..." : "Setup Permanent Deletion Cron"}
              </Button>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2">Premium Status Expiration</h3>
              <p className="text-sm text-gray-500 mb-2">
                Runs automatically at midnight to expire premium listings older than 28 days. You can also run it manually.
              </p>
              <Button 
                onClick={handlePremiumExpirationCheck}
                disabled={isPremiumExpirationLoading}
                variant="outline"
                className="w-full"
              >
                {isPremiumExpirationLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isPremiumExpirationLoading ? "Running..." : "Run Premium Expiration Check Now"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CronJobsManager;
